import React, { useState } from 'react';
import { AppStoreButton } from '../components/AppStoreButton';
import { StoreBadge } from '../components/StoreBadge';
import { OutlierBadge } from '../components/OutlierBadge';
import { AIInsightsCard } from '../components/AIInsightsCard';
import { AppIcon } from '../components/AppIcon';
import { BrandLogo } from '../components/BrandLogo';
import { CategoryIcon } from '../components/CategoryIcon';
import { Zap, Search, BarChart3, Sparkles, Building2, ArrowRight, Star, TrendingUp, Bookmark, Menu } from 'lucide-react';
import { APPS_DATA, DEVELOPERS_DATA, CATEGORIES_LIST, GLOBAL_AI_INSIGHTS } from '../data/mockData';
import { StoreType } from '../types';
import { getGrowthClassification } from '../components/GrowthScoreGauge';

interface HomeViewProps {
  onNavigate: (route: string) => void;
  onSelectApp: (appId: string) => void;
  onSelectDeveloper: (devSlug: string) => void;
}

export const HomeView: React.FC<HomeViewProps> = ({
  onNavigate,
  onSelectApp,
  onSelectDeveloper
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStore, setSelectedStore] = useState<StoreType>('all');

  const apps = Object.values(APPS_DATA);
  const developers = Object.values(DEVELOPERS_DATA);

  // Filtered preview list
  const filteredApps = apps.filter((a) => {
    if (selectedStore !== 'all' && !a.stores.includes(selectedStore as any)) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return (
      a.name.toLowerCase().includes(q) ||
      a.developer.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q)
    );
  });

  const breakoutApps = apps.filter((a) => a.growthClassification === 'Breakout');
  const fastestGrowing = apps.filter((a) => a.growthScore >= 85);
  const outlierApps = apps.filter((a) => a.outlier.isOutlier);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (filteredApps.length > 0) {
      onSelectApp(filteredApps[0].id);
    }
  };

  return (
    <main className="w-full max-w-full overflow-x-hidden">
      {/* 1. HERO SECTION (Keeping approved aesthetic, branding, typography & visual phone) */}
      <section className="relative text-center pt-12 pb-10 sm:pt-24 sm:pb-16 px-4 max-w-6xl mx-auto overflow-hidden">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-blue-900/10 shadow-sm text-xs font-semibold text-slate-600 mb-6 backdrop-blur-md">
          <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.15)] animate-pulse" />
          App intelligence, redesigned for the modern economy
        </div>

        {/* Headline */}
        <h1 className="text-3xl xs:text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tight text-slate-950 leading-[1.02] sm:leading-[0.96] max-w-5xl mx-auto mb-5 sm:mb-6">
          See what’s <span className="bg-gradient-to-r from-[#0c1834] via-[#146acb] to-[#56bfff] bg-clip-text text-transparent">winning</span>
          <br className="hidden sm:inline" /> on the App Store.
        </h1>

        {/* Subheadline */}
        <p className="text-sm sm:text-xl text-slate-600 max-w-3xl mx-auto font-normal leading-relaxed mb-6 sm:mb-8">
          Track rankings, growth velocity, reviews, competitors and breakout opportunities across the global app economy.
        </p>

        {/* Giant Search Engine Box */}
        <div className="max-w-2xl mx-auto mb-8 sm:mb-10 w-full">
          <form
            onSubmit={handleSearchSubmit}
            className="p-2 sm:p-2.5 rounded-3xl bg-white shadow-xl shadow-blue-950/5 border border-slate-200/90 flex flex-col sm:flex-row items-center gap-2 transition-all focus-within:ring-4 focus-within:ring-blue-500/10 focus-within:border-blue-500"
          >
            <div className="flex items-center gap-3 w-full px-3 py-1.5">
              <Search className="w-5 h-5 text-slate-400 flex-shrink-0" strokeWidth={2} />
              <input
                type="text"
                placeholder="Search any app, developer or keyword (e.g., Duolingo, ChatGPT)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-sm sm:text-base font-medium text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none"
              />
            </div>

            {/* Store Toggle & Search Button */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end px-2">
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value as StoreType)}
                className="text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-2 focus:outline-none"
              >
                <option value="all">All Stores</option>
                <option value="appstore">App Store</option>
                <option value="googleplay">Google Play</option>
              </select>

              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow-md transition-colors whitespace-nowrap"
              >
                Analyze App ↗
              </button>
            </div>
          </form>

          {/* Quick Search Chips */}
          <div className="flex items-center justify-center flex-wrap gap-1.5 mt-3 text-xs text-slate-500">
            <span className="font-semibold text-slate-400 mr-1">Popular:</span>
            {['Duolingo', 'ChatGPT', 'Spotify', 'Instagram', 'TikTok', 'Canva', 'Notion', 'CapCut'].map((appKey) => (
              <button
                key={appKey}
                onClick={() => onSelectApp(appKey.toLowerCase())}
                className="px-2.5 py-1 rounded-lg bg-slate-100/80 hover:bg-blue-50 hover:text-blue-600 transition-colors font-medium"
              >
                {appKey}
              </button>
            ))}
          </div>
        </div>

        {/* Download Buttons Row */}
        <div className="flex justify-center items-center gap-3 sm:gap-4 flex-wrap mb-10 sm:mb-12">
          <AppStoreButton />
          <button
            onClick={() => onNavigate('/trending')}
            className="h-[52px] px-6 rounded-2xl border border-slate-200 bg-white/70 hover:bg-white text-slate-900 font-bold text-sm inline-flex items-center gap-2 shadow-sm transition-all hover:shadow"
          >
            Explore Market Radar <span>↗</span>
          </button>
        </div>

        {/* 3D Visual Preview (Approved Phone Mockup with live interactive apps) */}
        <div className="relative max-w-4xl mx-auto h-[640px] sm:h-[720px] mt-4 sm:mt-6 overflow-hidden sm:overflow-visible">
          {/* Halo light backdrop */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] sm:w-[680px] h-[340px] sm:h-[400px] bg-gradient-to-tr from-sky-400/20 to-blue-600/10 blur-3xl pointer-events-none rounded-full" />

          {/* Floating Card 1: Growth Score */}
          <div className="hidden md:block absolute right-4 lg:right-12 top-24 z-20 bg-white/90 backdrop-blur-xl border border-white/80 p-4 rounded-3xl shadow-xl shadow-slate-900/10 text-left animate-bounce" style={{ animationDuration: '6s' }}>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Growth Score</span>
            <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
              96 <span className="text-xs text-slate-400 font-normal">/100</span>
            </div>
            <span className="text-xs font-bold text-emerald-600">▲ Breakout signal</span>
          </div>

          {/* Floating Card 2: Country rank */}
          <div className="hidden md:block absolute left-4 lg:left-12 bottom-36 z-20 bg-white/90 backdrop-blur-xl border border-white/80 p-4 rounded-3xl shadow-xl shadow-slate-900/10 text-left animate-bounce" style={{ animationDuration: '7s' }}>
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">United States 🇺🇸</span>
            <div className="text-3xl font-extrabold text-blue-600 tracking-tight">
              +126
            </div>
            <span className="text-xs text-slate-500">positions in 48h</span>
          </div>

          {/* Center Phone Shell */}
          <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[300px] sm:w-[350px] max-w-[calc(100vw-32px)] h-[620px] sm:h-[704px] rounded-[44px] sm:rounded-[58px] bg-[#090b0f] border border-black/80 p-2 sm:p-2.5 shadow-2xl shadow-slate-900/30">
            <div className="h-full w-full rounded-[44px] sm:rounded-[49px] overflow-hidden bg-gradient-to-b from-[#fbfdff] via-[#f3f7fb] to-[#eef4fa] p-4 text-left relative flex flex-col justify-between">
              {/* Dynamic Island */}
              <div className="w-24 h-6 rounded-full bg-black mx-auto mb-3" />

              {/* Inside Screen Header */}
              <div className="flex items-center justify-between pb-2">
                <BrandLogo size="xs" />
                <span className="w-6 h-6 rounded-full bg-gradient-to-tr from-blue-700 to-indigo-500" />
              </div>

              {/* Inside Search */}
              <div className="bg-white rounded-xl p-2.5 text-xs text-slate-400 border border-slate-200/80 shadow-2xs flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" strokeWidth={2} />
                <span>Search 2.4M+ apps...</span>
              </div>

              {/* Inside Metric */}
              <div className="bg-gradient-to-br from-[#0c172d] via-[#102e59] to-[#145da9] text-white p-3.5 rounded-2xl shadow-md my-2">
                <div className="flex justify-between text-[10px] text-sky-200 font-semibold">
                  <span>Breakout velocity</span>
                  <span>7-Day</span>
                </div>
                <div className="text-2xl font-black tracking-tight my-1">+38.4% <small className="text-xs font-normal opacity-80">growth</small></div>
                <div className="text-[10px] text-emerald-400 font-semibold">▲ 12.7% vs. category benchmark</div>
              </div>

              {/* Inside App List */}
              <div className="space-y-1.5 overflow-hidden flex-1">
                <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <span>Breakout Apps</span>
                  <span>Gain</span>
                </div>
                {apps.slice(0, 3).map((app) => (
                  <div
                    key={app.id}
                    onClick={() => onSelectApp(app.id)}
                    className="p-2 bg-white/95 rounded-xl border border-slate-200/60 shadow-2xs flex items-center justify-between cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <AppIcon app={app} size="xs" shape="squircle" className="w-8 h-8 rounded-lg" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-slate-900 truncate">{app.name}</div>
                        <div className="text-[10px] text-slate-400">{app.category} • #{app.currentRank}</div>
                      </div>
                    </div>
                    <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                      +{app.growthScore}%
                    </span>
                  </div>
                ))}
              </div>

              {/* Inside Bottom Mini Tab Bar (iOS style) */}
              <div className="pt-2 border-t border-slate-200/80 bg-white/80 -mx-4 -mb-4 px-4 py-2 grid grid-cols-5 text-center">
                <div className="flex flex-col items-center text-blue-600">
                  <Star className="w-4 h-4 fill-blue-600/20" strokeWidth={2} />
                  <span className="text-[9px] font-bold mt-0.5">Discover</span>
                </div>
                <div className="flex flex-col items-center text-slate-400">
                  <TrendingUp className="w-4 h-4" strokeWidth={1.75} />
                  <span className="text-[9px] mt-0.5">Trending</span>
                </div>
                <div className="flex flex-col items-center text-slate-400">
                  <Search className="w-4 h-4" strokeWidth={1.75} />
                  <span className="text-[9px] mt-0.5">Search</span>
                </div>
                <div className="flex flex-col items-center text-slate-400">
                  <Bookmark className="w-4 h-4" strokeWidth={1.75} />
                  <span className="text-[9px] mt-0.5">Watchlist</span>
                </div>
                <div className="flex flex-col items-center text-slate-400">
                  <Menu className="w-4 h-4" strokeWidth={1.75} />
                  <span className="text-[9px] mt-0.5">Menu</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. STATS STRIP (Approved layout with active metrics) */}
      <div className="border-y border-slate-200/80 bg-white/60 backdrop-blur-md py-8 my-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <strong className="block text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">190+</strong>
            <span className="text-xs text-slate-500 font-medium mt-1 block">Countries & Regions Indexed</span>
          </div>
          <div>
            <strong className="block text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">24h</strong>
            <span className="text-xs text-slate-500 font-medium mt-1 block">Live Ranking Updates</span>
          </div>
          <div>
            <strong className="block text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">0-100</strong>
            <span className="text-xs text-slate-500 font-medium mt-1 block">Proprietary Growth Score</span>
          </div>
          <div>
            <strong className="block text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">1 Platform</strong>
            <span className="text-xs text-slate-500 font-medium mt-1 block">For Total App Intelligence</span>
          </div>
        </div>
      </div>

      {/* 3. BREAKOUT & TRENDING APPS SHOWCASE */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Market Movement</span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight mt-1">
              Breakout Apps Trending Now
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Apps exhibiting aggressive ranking velocity and sudden category surges.
            </p>
          </div>
          <button
            onClick={() => onNavigate('/trending')}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 self-start md:self-auto"
          >
            View all trending apps ↗
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {breakoutApps.slice(0, 6).map((app) => {
            const { label, bg } = getGrowthClassification(app.growthScore);
            return (
              <div
                key={app.id}
                onClick={() => onSelectApp(app.id)}
                className="bg-white rounded-3xl p-5 border border-slate-200/80 hover:border-blue-300 hover:shadow-lg shadow-sm transition-all duration-200 cursor-pointer group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <AppIcon
                        app={app}
                        size="md"
                        className="w-12 h-12 rounded-2xl shadow-sm group-hover:scale-105 transition-transform"
                      />
                      <div>
                        <h3 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors line-clamp-1">
                          {app.name}
                        </h3>
                        <p className="text-xs text-slate-400">{app.developer}</p>
                      </div>
                    </div>
                    <StoreBadge store={app.primaryStore} />
                  </div>

                  <p className="text-xs text-slate-600 line-clamp-2 mb-4 leading-relaxed">
                    {app.subtitle || app.description}
                  </p>
                </div>

                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-slate-400 block text-[10px] uppercase font-semibold">Store Rank</span>
                    <strong className="text-slate-900 font-extrabold">#{app.currentRank}</strong>
                    <span className="text-emerald-600 font-semibold ml-1.5">▲ {app.growthVelocityPct}%</span>
                  </div>

                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${bg}`}>
                    Growth {app.growthScore} • {label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. APP OUTLIERS RADAR SECTION */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="bg-gradient-to-br from-slate-900 via-[#0a152d] to-[#0d346b] rounded-3xl p-6 sm:p-10 text-white shadow-xl relative overflow-hidden">
          <div className="max-w-2xl mb-8 relative z-10">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 text-xs font-bold mb-3">
              <Zap className="w-3.5 h-3.5" strokeWidth={2} />
              <span>Proprietary Anomaly Detection</span>
            </div>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white mb-2">
              App Outliers Radar
            </h2>
            <p className="text-sm text-slate-300 leading-relaxed">
              Our algorithms detect apps whose install velocity and ranking trajectory deviate 200%+ from their 30-day baseline before they become obvious to everyone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
            {outlierApps.map((app) => (
              <div
                key={app.id}
                onClick={() => onSelectApp(app.id)}
                className="bg-white/10 hover:bg-white/15 border border-white/10 backdrop-blur-md rounded-2xl p-5 transition-all cursor-pointer group"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <AppIcon app={app} size="sm" className="w-10 h-10 rounded-xl" />
                    <div>
                      <h4 className="font-bold text-white text-sm group-hover:text-sky-300 transition-colors">
                        {app.name}
                      </h4>
                      <span className="text-xs text-slate-400">{app.category}</span>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                    {app.outlier.level.toUpperCase()}
                  </span>
                </div>

                <div className="text-xs text-sky-200 font-bold mb-1">
                  Surging {app.outlier.multiplier}× above 30-day baseline
                </div>
                <p className="text-[11px] text-slate-300 line-clamp-2 mb-3">
                  {app.outlier.summary}
                </p>

                <div className="flex justify-between items-center text-[10px] pt-2 border-t border-white/10 text-slate-400 font-medium">
                  <span>Current: <strong className="text-emerald-400">{app.outlier.currentVelocity}</strong></span>
                  <span className="text-sky-300 font-semibold group-hover:underline inline-flex items-center gap-1">Analyze Outlier <ArrowRight className="w-3 h-3" strokeWidth={2} /></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. CATEGORY INTELLIGENCE HUB */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Browse Economy</span>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-1">Category Intelligence</h2>
          </div>
          <button
            onClick={() => onNavigate('/charts')}
            className="text-xs font-bold text-blue-600 hover:underline inline-flex items-center gap-1"
          >
            Explore all charts <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {CATEGORIES_LIST.slice(1).map((cat) => (
            <button
              key={cat.slug}
              onClick={() => onNavigate('/charts')}
              className="p-4 rounded-2xl bg-white border border-slate-200/80 hover:border-blue-400 hover:shadow-md transition-all text-left group"
            >
              <div className="w-9 h-9 rounded-xl bg-slate-100/90 text-slate-700 flex items-center justify-center mb-3 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                <CategoryIcon slug={cat.slug} name={cat.name} size={18} strokeWidth={1.75} />
              </div>
              <span className="font-bold text-slate-900 text-xs block group-hover:text-blue-600 transition-colors">
                {cat.name}
              </span>
              <span className="text-[11px] text-slate-400">{cat.appCount} apps</span>
            </button>
          ))}
        </div>
      </section>

      {/* 6. DEVELOPERS SPOTLIGHT */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-blue-600">Top Studios</span>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-1">Trending Developers</h2>
          </div>
          <button
            onClick={() => onNavigate('/developers')}
            className="text-xs font-bold text-blue-600 hover:underline"
          >
            All developers ↗
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {developers.slice(0, 4).map((dev) => (
            <div
              key={dev.id}
              onClick={() => onSelectDeveloper(dev.slug)}
              className="p-5 rounded-3xl bg-white border border-slate-200/80 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-3 mb-3">
                <AppIcon
                  src={dev.avatarUrl}
                  name={dev.name}
                  size="sm"
                  shape="squircle"
                  className="w-10 h-10 rounded-xl flex-shrink-0"
                />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors">
                    {dev.name}
                  </h4>
                  <span className="text-xs text-slate-400">{dev.hqCountry}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs py-2 border-y border-slate-100 my-2">
                <div>
                  <span className="text-slate-400 block text-[10px]">Combined Est.</span>
                  <strong className="text-slate-800">{(dev.combinedEstimatedDownloads / 1000000).toFixed(1)}M dl</strong>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">Growth Score</span>
                  <strong className="text-blue-600 font-bold">{dev.growthScore} / 100</strong>
                </div>
              </div>

              <span className="text-xs text-blue-600 font-bold block text-right mt-1">
                View Portfolio ›
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* 7. AI INTELLIGENCE STREAM */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <AIInsightsCard
          insights={GLOBAL_AI_INSIGHTS}
          title="AppStatsView AI — Live Market Signals"
          onSelectApp={onSelectApp}
        />
      </section>

      {/* 8. ORIGINAL APPROVED FEATURES GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16" id="features">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-wider text-blue-600">App Intelligence</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-1 mb-3">
            Everything that matters. Nothing that doesn’t.
          </h2>
          <p className="text-base text-slate-600">
            Built for founders, developers, marketers and investors who want to understand which apps are moving — and why.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <article className="p-7 rounded-3xl bg-white/80 border border-slate-200/80 shadow-sm relative overflow-hidden">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl font-bold mb-4">
              ↗
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Breakout Apps</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Spot unusual ranking velocity before an app becomes obvious to everyone else.
            </p>
          </article>

          <article className="p-7 rounded-3xl bg-gradient-to-br from-[#081121] to-[#10284c] text-white shadow-md relative overflow-hidden">
            <div className="w-12 h-12 rounded-2xl bg-white/10 text-sky-400 flex items-center justify-center text-xl font-bold mb-4">
              ◎
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Growth Score</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              A simple 0–100 signal combining momentum, ranking shifts and category-relative performance.
            </p>
          </article>

          <article className="p-7 rounded-3xl bg-white/80 border border-slate-200/80 shadow-sm relative overflow-hidden">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Search className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Competitor Radar</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Compare rankings, ratings, releases and market movement across apps and developers.
            </p>
          </article>

          <article className="p-7 rounded-3xl bg-white/80 border border-slate-200/80 shadow-sm relative overflow-hidden">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <BarChart3 className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Global Charts</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Explore top apps by country, category and time period without jumping between storefronts.
            </p>
          </article>

          <article className="p-7 rounded-3xl bg-white/80 border border-slate-200/80 shadow-sm relative overflow-hidden">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">AI Insights</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Turn raw market movement into clear explanations, anomalies and opportunities worth watching.
            </p>
          </article>

          <article className="p-7 rounded-3xl bg-white/80 border border-slate-200/80 shadow-sm relative overflow-hidden">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Developer Profiles</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              See a developer’s portfolio, momentum and launches through one clean market-intelligence view.
            </p>
          </article>
        </div>
      </section>

      {/* 9. CTA SECTION (Approved design) */}
      <section className="max-w-7xl mx-auto px-6 py-12" id="download">
        <div className="rounded-[40px] bg-gradient-to-tr from-[#0a1630] via-[#123b6f] to-[#0d72ca] p-8 sm:p-16 text-white relative overflow-hidden shadow-2xl">
          <div className="max-w-xl relative z-10">
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
              The app economy, at a glance.
            </h2>
            <p className="text-base sm:text-lg text-sky-100 mb-8 leading-relaxed">
              Download AppStatsView and discover what’s rising, what’s fading and what deserves your attention.
            </p>
            <AppStoreButton variant="white" />
          </div>

          {/* Decorative glass orb */}
          <div className="absolute -right-16 -bottom-16 w-80 h-80 rounded-full bg-gradient-to-br from-white/30 to-sky-400/10 backdrop-blur-md border border-white/20 pointer-events-none" />
        </div>
      </section>
    </main>
  );
};
